import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { IconButton } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageContent } from "../components/PageContent";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../components/Table";
import { CatalogListName } from "../components/CatalogOriginChip";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { useShowCatalogOriginMarkers } from "../hooks/useShowCatalogOriginMarkers";
import {
  useGetLayoutsQuery,
  useDeleteLayoutMutation,
} from "../query/layoutsQuery";
import { useGetProductPagesQuery } from "../query/productPagesQuery";
import type { LayoutId } from "@KassaroAB/shared/types/ids";

export const LayoutsView = () => {
  const { t } = useTranslation();
  const { scope, pagePath } = useCatalogScope("layouts");
  const showCatalogOrigin = useShowCatalogOriginMarkers(scope);

  const [deleteId, setDeleteId] = useState<LayoutId | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    data: layouts,
    isLoading,
    error: loadError,
  } = useGetLayoutsQuery(scope);
  const { data: productPages } = useGetProductPagesQuery(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteLayoutMutation(scope);

  const layoutList = layouts ?? [];
  const deleteName =
    layoutList.find((layout) => layout.id === deleteId)?.name ?? "";

  const productPageName = (productPageId: string | null) => {
    if (!productPageId) return t("layoutsView.noDefaultPage");
    return (
      productPages?.find((page) => page.id === productPageId)?.name ??
      productPageId
    );
  };

  const confirmDelete = () => {
    if (!deleteId) return;
    deleteMutation(deleteId, {
      onSuccess: () => {
        setSuccessMessage(t("layoutsView.successDeleted"));
        setDeleteId(null);
      },
      onError: () => setSuccessMessage(null),
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  if (isLoading && layoutList.length === 0) {
    return (
      <PageContent title={t("layoutsView.title")}>
        <LoadingContainer message={t("layoutsView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent title={t("layoutsView.title")}>
      {loadError && (
        <InfoBox variant="error">{t("layoutsView.errorLoad")}</InfoBox>
      )}
      {deleteError && (
        <InfoBox variant="error">{t("layoutsView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      {layoutList.length > 0 ? (
        <Card>
          <Table minWidth="640px">
            <TableHeader>
              <tr>
                <TableHeaderCell>{t("layoutsView.tableName")}</TableHeaderCell>
                <TableHeaderCell>
                  {t("layoutsView.tableDefaultPage")}
                </TableHeaderCell>
                <TableHeaderCell align="right">{null}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {layoutList.map((layout) => (
                <TableRow key={layout.id} to={`${pagePath}/${layout.id}`}>
                  <TableCell noWrap>
                    <CatalogListName
                      name={layout.name}
                      origin={layout.origin}
                      showOrigin={showCatalogOrigin}
                    />
                  </TableCell>
                  <TableCell noWrap>
                    {productPageName(layout.defaultProductPageId)}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      type="button"
                      $color="danger"
                      aria-label={t("common.delete")}
                      disabled={isDeletePending}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setDeleteId(layout.id);
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <EmptyState message={t("layoutsView.empty")} />
        </Card>
      )}
      <ConfirmDialog
        isOpen={deleteId != null}
        title={t("layoutForm.deleteLayout")}
        message={t("layoutsView.deleteConfirm", { name: deleteName })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </PageContent>
  );
};
